import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Plus, Pencil, Trash2, ExternalLink, ChevronUp, ChevronDown, X, FileText } from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import {
  fetchPolicies,
  createPolicy,
  updatePolicy,
  deletePolicy,
  clearPolicyError,
} from './services/policySlice'

const SITE_URL = 'https://supadaa.com'

const makeSlug = (text) =>
  text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

const EMPTY_SECTION = { heading: '', body: '' }
const EMPTY_FORM = {
  title: '', slug: '', subtitle: '', meta_description: '',
  sort_order: '', status: 1,
  sections: [{ ...EMPTY_SECTION }],
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—')

export default function Policies() {
  const dispatch = useDispatch()
  const { list: policies, isLoading, isSaving, isError, message, errors } =
    useSelector((state) => state.policy)

  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  useEffect(() => { dispatch(fetchPolicies()) }, [dispatch])

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }))

  const fieldError = (name) =>
    errors?.[name] ? <small style={{ color: '#ef4444' }}>{errors[name][0]}</small> : null

  const sectionError = Object.keys(errors || {}).find((k) => k.startsWith('sections'))

  /* ── sections editor ── */
  const setSection = (i, key, value) =>
    setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)) }))
  const addSection = () => setForm((f) => ({ ...f, sections: [...f.sections, { ...EMPTY_SECTION }] }))
  const removeSection = (i) =>
    setForm((f) => ({ ...f, sections: f.sections.length > 1 ? f.sections.filter((_, idx) => idx !== i) : f.sections }))
  const moveSection = (i, dir) =>
    setForm((f) => {
      const j = i + dir
      if (j < 0 || j >= f.sections.length) return f
      const next = [...f.sections]
      ;[next[i], next[j]] = [next[j], next[i]]
      return { ...f, sections: next }
    })

  const openAdd = () => {
    dispatch(clearPolicyError())
    setActive(null)
    setForm({ ...EMPTY_FORM, sections: [{ ...EMPTY_SECTION }] })
    setModalOpen(true)
  }

  const openEdit = (p) => {
    dispatch(clearPolicyError())
    setActive(p)
    setForm({
      title: p.title,
      slug: p.slug,
      subtitle: p.subtitle || '',
      meta_description: p.meta_description || '',
      sort_order: p.sort_order,
      status: p.status,
      sections: p.sections?.length ? p.sections.map((s) => ({ ...s })) : [{ ...EMPTY_SECTION }],
    })
    setModalOpen(true)
  }

  const save = async () => {
    if (!form.title.trim()) return
    const sections = form.sections.filter((s) => s.heading.trim() || s.body.trim())
    if (sections.length === 0) return

    const payload = {
      ...form,
      sections,
      sort_order: form.sort_order === '' ? undefined : Number(form.sort_order),
      status: Number(form.status),
    }
    const result = active
      ? await dispatch(updatePolicy({ id: active.id, ...payload }))
      : await dispatch(createPolicy(payload))
    if (result.meta.requestStatus === 'fulfilled') setModalOpen(false)
  }

  const onDelete = async () => {
    const result = await dispatch(deletePolicy(active.id))
    if (result.meta.requestStatus === 'fulfilled') {
      setDeleteOpen(false)
      setActive(null)
    }
  }

  return (
    <div className="dash-page page-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
        <div>
          <h1 className="page-title">Policies</h1>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Policy</button>
      </div>

      {isError && message && !modalOpen && <div className="login-error">{message}</div>}

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr><th>Page</th><th>Website URL</th><th>Sections</th><th>Updated</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading…</td></tr>}
              {!isLoading && policies.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text3)' }}>No policies yet</td></tr>
              )}
              {policies.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                      <FileText size={16} style={{ color: 'var(--text3)', flexShrink: 0 }} />
                      <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{p.title}</div>
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>
                    <a href={`${SITE_URL}${p.url}`} target="_blank" rel="noreferrer"
                      style={{ color: 'var(--accent-hover)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {p.url} <ExternalLink size={11} />
                    </a>
                  </td>
                  <td>{p.sections?.length || 0}</td>
                  <td style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{fmtDate(p.updated_at)}</td>
                  <td>
                    <span className={`badge ${p.status === 1 ? 'badge-active' : 'badge-inactive'}`}>
                      {p.status === 1 ? 'published' : 'hidden'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(p)} aria-label="Edit"><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(p); setDeleteOpen(true) }} aria-label="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={active ? 'Edit Policy' : 'Add Policy'}
        subtitle="Page title, intro and sections will be shown on the website"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : active ? 'Update Policy' : 'Create Policy'}
            </button>
          </>
        )}
      >
        {isError && message && <div className="login-error" style={{ marginBottom: 12 }}>{message}</div>}

        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Title</label>
            <input className="input" placeholder="e.g. Shipping Policy" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value, slug: active ? form.slug : makeSlug(e.target.value) })} />
            {fieldError('title')}
          </div>
          <div className="form-field">
            <label>Slug <small style={{ color: 'var(--text3)' }}>(don't change it, footer links depend on it)</small></label>
            <input className="input" placeholder="shipping-policy" value={form.slug}
              onChange={(e) => setField('slug', makeSlug(e.target.value))} />
            {fieldError('slug')}
          </div>

          <div className="form-field full">
            <label>Intro <small style={{ color: 'var(--text3)' }}>(shown at the top of the page, below the heading)</small></label>
            <textarea className="input" rows={2} value={form.subtitle}
              onChange={(e) => setField('subtitle', e.target.value)} />
            {fieldError('subtitle')}
          </div>
          <div className="form-field full">
            <label>Meta Description <small style={{ color: 'var(--text3)' }}>(for Google, optional)</small></label>
            <textarea className="input" rows={2} value={form.meta_description}
              onChange={(e) => setField('meta_description', e.target.value)} />
            {fieldError('meta_description')}
          </div>

          <div className="form-field">
            <label>Sort Order</label>
            <input type="number" min={0} className="input" value={form.sort_order}
              onChange={(e) => setField('sort_order', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Status</label>
            <select className="input" value={form.status} onChange={(e) => setField('status', Number(e.target.value))}>
              <option value={1}>Published</option>
              <option value={0}>Hidden (404 on website)</option>
            </select>
          </div>

          {/* ---- Sections ---- */}
          <div className="form-field full">
            <label>Sections</label>
            <div className="pol-hint">
              Blank line = new paragraph · <code>- text</code> = bullet · <code>1. text</code> = numbered ·
              <code>&gt; text</code> = highlighted note · <code>[text](/contact_us)</code> = link · <code>**bold**</code>
            </div>

            {sectionError && <small style={{ color: '#ef4444' }}>{errors[sectionError][0]}</small>}

            <div className="pol-sections">
              {form.sections.map((s, i) => (
                <div key={i} className="pol-section">
                  <div className="pol-section-head">
                    <span className="pol-section-no">{i + 1}</span>
                    <input className="input" placeholder="Section heading" value={s.heading}
                      onChange={(e) => setSection(i, 'heading', e.target.value)} />
                    <button type="button" className="btn btn-ghost btn-icon" onClick={() => moveSection(i, -1)} disabled={i === 0} aria-label="Move up"><ChevronUp size={14} /></button>
                    <button type="button" className="btn btn-ghost btn-icon" onClick={() => moveSection(i, 1)} disabled={i === form.sections.length - 1} aria-label="Move down"><ChevronDown size={14} /></button>
                    <button type="button" className="btn btn-ghost btn-icon" onClick={() => removeSection(i)} disabled={form.sections.length === 1} aria-label="Remove section"><X size={14} /></button>
                  </div>
                  <textarea className="input" rows={6} placeholder="Section text…" value={s.body}
                    onChange={(e) => setSection(i, 'body', e.target.value)} />
                </div>
              ))}
              <button type="button" className="btn btn-outline btn-sm" onClick={addSection}><Plus size={13} /> Add section</button>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete policy"
        message={`Delete "${active?.title || 'selected policy'}"? The website page will show a 404.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}
        textarea.input{resize:vertical;min-height:56px;}
        .login-error{background:rgba(239,68,68,.12);border:1px solid rgba(239,68,68,.35);color:#fca5a5;font-size:.875rem;padding:.6rem .9rem;border-radius:10px;}
        .pol-hint{font-size:var(--fs-xs);color:var(--text3);line-height:1.7;margin-bottom:8px;}
        .pol-hint code{background:var(--bg-hover);padding:1px 5px;border-radius:4px;font-size:11px;}
        .pol-sections{display:flex;flex-direction:column;gap:12px;align-items:flex-start;width:100%;}
        .pol-section{width:100%;border:1px solid var(--border);border-radius:var(--radius-md);padding:10px;background:var(--bg-raised);display:flex;flex-direction:column;gap:8px;}
        .pol-section-head{display:flex;align-items:center;gap:6px;}
        .pol-section-no{flex-shrink:0;width:24px;height:24px;border-radius:50%;display:grid;place-items:center;font-size:11px;font-weight:700;background:var(--bg-active);color:var(--accent-hover);}
      `}</style>
    </div>
  )
}